import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { WARRANTY_STARTS_FROM, WARRANTY_YEARS, WARRANTY_COVER } from '../config/warranty';
import { ZONE2_FEE_CENTS } from '../utils/delivery';
import { LEAD_STOCK, LEAD_STANDARD, LEAD_LARGE, LEAD_PERGOLA, STOCK_COLOUR_NAME, STANDARD_SIZE_MAX_M } from '../config/leadTimes';
import './LegalPage.css';

function TermsPage() {
  // Figures come from the same constants the storefront uses, so the terms
  // cannot quietly drift away from what the site advertises.
  const zone2Fee = `$${(ZONE2_FEE_CENTS / 100).toFixed(0)}`;

  return (
    <div className="legal-page">
      <SEO
        title="Terms & Conditions"
        description="ASG Shading terms and conditions - terms of use for our website and services including quotes, supply, delivery and warranties."
        canonical="/terms"
        jsonLd={[SEO.breadcrumbSchema([{ name: 'Terms & Conditions' }]), SEO.webPageSchema({ type: 'WebPage', name: 'Terms & Conditions', description: 'ASG Shading terms and conditions.', url: '/terms' })]}
      />
      <section className="legal-hero">
        <div className="legal-hero-bg"></div>
        <div className="legal-hero-content">
          <p className="legal-label">Legal</p>
          <h1 className="legal-title">Terms &amp; Conditions</h1>
          <p className="legal-updated">Last updated: 1 September 2026</p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Terms & Conditions' }]} />

      <section className="legal-content">
        <div className="legal-inner">

          <h2>1. About These Terms</h2>
          <p>
            These terms and conditions govern your use of the ASG Shading website
            (asgshading.com.au) and our services. ASG Shading (ABN 54 658 871 369) operates
            from Melbourne, Victoria, Australia. By using this website or engaging our services,
            you agree to these terms.
          </p>

          <h2>2. Website Use</h2>
          <p>You may use this website for lawful purposes only. You agree not to:</p>
          <ul>
            <li>Use the site in any way that breaches applicable laws or regulations</li>
            <li>Attempt to gain unauthorised access to any part of the website or server</li>
            <li>Use automated tools to scrape, crawl or extract data without permission</li>
            <li>Transmit any material that is defamatory, offensive or otherwise objectionable</li>
          </ul>

          <h2>3. Quotes and Pricing</h2>
          <ul>
            <li>All quotes provided through this website are estimates only and are subject to confirmation of final dimensions and specifications</li>
            <li>Quotes are valid for 30 days from the date of issue unless otherwise stated</li>
            <li>Prices are in Australian dollars (AUD) and include GST unless stated otherwise</li>
            <li>Final pricing may vary based on final dimensions, material availability, and scope changes</li>
            <li>A deposit is required to confirm your order - the amount will be specified in your formal quote</li>
          </ul>

          <h2>4. Supply-Only Scope</h2>
          <p>
            ASG Shading supplies goods only. We do not install, and installation does not form
            part of any contract with us.
          </p>
          <ul>
            <li>We do not install any product we supply</li>
            <li>We do not attend, inspect or assess installation sites, and we do not carry out site visits or at-home consultations</li>
            <li>We do not apply for, manage or advise on council approvals, building permits or engineering certification</li>
            <li>Installation is arranged by you, at your cost. Awnings may be fitted by you, a handyman or an installer of your choice; pergolas require a suitably qualified and licensed builder. Any new power point must be installed by a licensed electrician</li>
          </ul>
          <p>
            To the extent permitted by law, we accept no liability for installation carried out by
            you or by any third party, or for any loss, failure or damage arising from it. This
            includes, without limitation, incorrect fixing, fixings unsuitable for the substrate,
            an unsuitable or inadequate substrate or supporting structure, and installation carried
            out contrary to the installation manual supplied with the goods. Nothing in this clause
            limits your rights under the Australian Consumer Law.
          </p>

          <h2>5. Orders and Made-to-Measure Goods</h2>
          <ul>
            <li>An order is accepted when we confirm it in writing and payment (or any required deposit) has been received</li>
            <li>Most goods we supply are made to measure and are manufactured to the dimensions, fabric and finish you specify</li>
            <li>A deposit of 50% may be required on large or custom orders, with the balance payable before dispatch</li>
          </ul>
          <p>
            Once fabrication has commenced, made-to-measure goods - including custom sizes, custom
            fabrics and custom finishes - cannot be cancelled, returned or exchanged for change of
            mind, and any deposit paid may be non-refundable from that point. This applies to any
            unit built to dimensions you supplied: for example, an awning ordered at a custom width
            of 4.3 metres is cut, sewn and assembled to that measurement, cannot be resold to
            another customer, and is not returnable because you change your mind or because it does
            not suit the space. We will tell you when
            fabrication is scheduled to commence if you ask before placing your order. This clause
            does not apply where goods are faulty or otherwise fail to meet a consumer guarantee.
          </p>

          <h2>6. Dispatch and Delivery</h2>
          <p><strong>Dispatch timeframes.</strong> Dispatch timeframes are estimates, measured from order confirmation and receipt of cleared payment:</p>
          <ul>
            <li>{STOCK_COLOUR_NAME} awning fabric held in stock: often dispatched the {LEAD_STOCK}</li>
            <li>Made-to-order awnings up to {STANDARD_SIZE_MAX_M} metres in width and projection: {LEAD_STANDARD}</li>
            <li>Made-to-order awnings above {STANDARD_SIZE_MAX_M} metres in width or projection: {LEAD_LARGE}</li>
            <li>Aluminium pergola systems: {LEAD_PERGOLA}</li>
          </ul>
          <p>
            Timeframes may be affected by material availability, freight schedules and manufacturing
            capacity. We will tell you as soon as reasonably practicable if a delay arises.
          </p>

          <p><strong>Delivery zones and charges.</strong> Delivery is charged by zone:</p>
          <ul>
            <li><strong>Free delivery</strong> to most of Melbourne, which we deliver ourselves</li>
            <li><strong>{zone2Fee} flat rate</strong> to the outer Melbourne suburbs and the near-regional edge</li>
            <li><strong>Freight quoted separately</strong> everywhere else in Australia. We will confirm the freight cost with you before any charge is made, and orders to these areas cannot be completed at online checkout</li>
          </ul>
          <p>
            The zones are defined by postcode. You can check your postcode on our{' '}
            <Link to="/delivery">delivery page</Link>, which sets out the current zone boundaries.
            We do not currently deliver outside Australia.
          </p>

          <p><strong>Delivery conditions.</strong> The following apply to every delivery:</p>
          <ul>
            <li>Delivery is <strong>to the door</strong> - to the ground-floor entrance of the delivery address. We do not carry goods inside, upstairs, onto a roof, or to any other position on the property</li>
            <li>A <strong>signature is required</strong> on delivery. Goods will not be left unattended unless you have given us prior authority to leave (see below)</li>
            <li><strong>Someone aged 18 or over must be present</strong> to receive and sign for the goods</li>
            <li><strong>Units over 3 metres in width or projection require two people</strong> to be present to receive them safely. Receiving the goods is separate from installing them; installation requires at least two people, and three or more for units 4 metres wide or more, as set out in the installation manual</li>
            <li>You must provide safe and reasonable access for delivery</li>
          </ul>
          <p>
            If nobody is present, if there are not enough people present to receive a large unit
            safely, or if access is unsafe or inadequate, the delivery may not be completed. Where
            that happens, you are responsible for the <strong>actual cost of redelivery</strong>.
            Please tell us before dispatch if access is restricted.
          </p>
          <p>
            <strong>Authority to leave.</strong> If you confirm to us in writing that the goods may
            be left, we may leave them unattended at the front of the delivery address without a
            signature. If you give that authority:
          </p>
          <ul>
            <li>Delivery is treated as complete when the goods are left, and <strong>risk passes to you at that point</strong> (see clause 7)</li>
            <li>We are not responsible for loss, theft, weather damage or damage occurring after the goods are left</li>
            <li>The inspection and transit-damage timeframes in clause 8 run from the date the goods are left</li>
          </ul>
          <p>
            We may decline to leave goods unattended where we consider it unsafe or unsuitable, for
            example a large or heavy unit, an exposed location, or a location visible from the
            street. Nothing in this clause limits your rights under the Australian Consumer Law.
          </p>

          <h2>7. Risk and Title</h2>
          <ul>
            <li><strong>Risk</strong> in the goods passes to you on delivery</li>
            <li><strong>Title</strong> in the goods does not pass to you until we have received payment in full of all amounts owing for those goods</li>
            <li>Until title passes, you must store the goods so they remain identifiable as ours and must not dispose of or encumber them</li>
          </ul>

          <h2>8. Damage in Transit</h2>
          <p>
            You must inspect the goods on delivery, before signing, and check the packaging and the
            goods for visible damage.
          </p>
          <ul>
            <li>Any damage, shortage or incorrect item must be reported to us <strong>in writing</strong> at <a href="mailto:info@asgshading.com.au">info@asgshading.com.au</a> within <strong>2 business days</strong> of delivery</li>
            <li>Your report must include <strong>photographs</strong> of the damage and of the packaging as received</li>
            <li>Please keep the original packaging until the matter is resolved, as freight carriers may require it to assess a claim</li>
            <li>Where possible, note any visible damage on the carrier's delivery documentation before signing</li>
          </ul>
          <p>
            Reporting outside this period may limit our ability to claim against the freight carrier,
            but it does not limit your rights under the Australian Consumer Law.
          </p>

          <h2>9. Measurements and Remote Advice</h2>
          <p>
            You are responsible for the accuracy of all dimensions, measurements and site details you
            supply to us. Made-to-measure goods are manufactured to those figures, and goods
            manufactured to dimensions you supplied are not returnable for change of mind if they do
            not suit the space.
          </p>
          <p>
            Because we are supply-only, we often advise remotely from photographs, measurements and
            other information you provide. That advice is general in nature and is given in good
            faith on the basis of the information supplied. It is not a site inspection, a
            measurement service, a structural assessment or engineering advice, and it does not
            transfer responsibility for the accuracy of measurements or the suitability of the
            structure to us. If you are unsure, please have your installer or builder confirm
            measurements and the suitability of the substrate before you order.
          </p>

          <h2>10. Wind, Weather and Operating Limits</h2>
          <p>
            Retractable awnings are <strong>shade products</strong>. They are not designed as
            permanent all-weather structures and must not be treated as shelter from wind, hail,
            snow or storms.
          </p>
          <ul>
            <li>The AW01 Full Cassette Awning has been wind tested by Intertek to <strong>EN 13561 Class 3</strong>, corresponding to Beaufort force 6 (approximately 39 to 49 km/h). That result applies to the sample tested, at the tested size of 3000mm x 1530mm, and does not extend to other sizes or models</li>
            <li>No other model in our range carries independent wind test data, and no wind resistance claim is made for those models</li>
            <li>The awning <strong>must be retracted</strong> in wind above Beaufort force 6, and in hail or snow</li>
            <li>The awning must also be retracted when unattended for an extended period, and where weather conditions are forecast to exceed these limits</li>
          </ul>
          <p>
            Damage caused by leaving a unit extended in wind above the rated limit, or in hail or
            snow, is <strong>not covered by warranty</strong>. These limits match the operating
            limits in the installation manual supplied with the goods; where this clause and the
            manual differ, the manual applies. A wind sensor, where fitted, is an aid only and does
            not remove your responsibility to retract the awning.
          </p>

          <h2>11. Warranty</h2>
          <p>
            ASG Shading provides a {WARRANTY_YEARS}-year warranty on all products supplied,
            running from the {WARRANTY_STARTS_FROM}. The Dooya motor and electrical components are
            covered by their respective manufacturer warranties, separate from this warranty. Our
            warranty covers:
          </p>
          <ul>
            <li>Awnings: {WARRANTY_COVER.awning.covers}</li>
            <li>Pergolas: structural integrity of the supplied pergola system</li>
            <li>Defects in materials and workmanship under normal use</li>
          </ul>
          <p>The warranty does not cover:</p>
          <ul>
            <li>Damage caused by misuse, neglect, or unauthorised modifications</li>
            <li>Normal wear and tear, including minor cosmetic changes</li>
            <li>Damage caused by extreme weather events beyond design specifications</li>
            <li>Issues arising from non-compliance with maintenance guidelines</li>
          </ul>
          <p>
            This warranty is in addition to your rights under the Australian Consumer Law,
            which cannot be excluded.
          </p>

          <h2>12. Payment Terms</h2>
          <ul>
            <li>Payment schedules will be outlined in your formal quote</li>
            <li>For large custom orders, a deposit may be required to confirm your order, with the balance due before dispatch</li>
            <li>Full payment is due before dispatch</li>
            <li>We accept bank transfer and other methods as agreed</li>
          </ul>

          <h2>13. Cancellation and Change of Mind</h2>
          <ul>
            <li>Cancellations must be made in writing to <a href="mailto:info@asgshading.com.au">info@asgshading.com.au</a></li>
            <li>Deposits may be non-refundable if materials have been ordered or fabrication has commenced</li>
            <li>Made-to-measure goods cannot be cancelled for change of mind once fabrication has commenced (see clause 5)</li>
            <li>Stock items may be returned for change of mind within <strong>14 days</strong> of delivery, on the conditions set out below</li>
            <li>Your rights under the Australian Consumer Law are not affected by this clause</li>
          </ul>
          <p>
            <strong>Change of mind on stock items.</strong> Where an item was supplied from stock
            and was not made to measure, you may return it for change of mind within 14 days of
            delivery, provided that:
          </p>
          <ul>
            <li>The goods are unused, undamaged and in their original, unopened packaging</li>
            <li>You contact us in writing at <a href="mailto:info@asgshading.com.au">info@asgshading.com.au</a> within 14 days of delivery to arrange the return</li>
            <li>You pay the cost of returning the goods to us, and the goods reach us in resaleable condition</li>
            <li>Any original delivery charge paid is not refunded</li>
          </ul>
          <p>
            Most of what we supply is made to measure and is therefore not eligible for a
            change-of-mind return once fabrication has commenced (see clause 5). In particular,
            <strong>custom sizes are never accepted back for change of mind</strong> - a unit built
            to your own dimensions, such as an awning at 4.3 metres, cannot be returned or
            exchanged on this basis. This
            change-of-mind policy is offered in addition to, and does not limit, your rights under
            the Australian Consumer Law, which apply regardless of the condition of the packaging.
          </p>

          <h2>14. Australian Consumer Law</h2>
          <p>
            Our goods come with guarantees that cannot be excluded under the Australian Consumer
            Law. You are entitled to a replacement or refund for a major failure and to
            compensation for any other reasonably foreseeable loss or damage. You are also entitled
            to have the goods repaired or replaced if the goods fail to be of acceptable quality and
            the failure does not amount to a major failure.
          </p>
          <p>
            Nothing in these terms excludes, restricts or modifies any guarantee, right or remedy
            you have under the Australian Consumer Law or any other law that cannot lawfully be
            excluded, restricted or modified. Where any part of these terms would do so, that part
            applies only to the extent the law permits. Where our liability may lawfully be limited,
            it is limited at our option to replacing or repairing the goods, resupplying equivalent
            goods, or paying the cost of doing so.
          </p>

          <h2>15. Intellectual Property</h2>
          <p>
            All content on this website - including text, images, designs, logos and code - is
            owned by ASG Shading or our licensors and is protected by Australian copyright law.
            You may not reproduce, distribute or modify any content without our written permission.
          </p>

          <h2>16. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, ASG Shading is not liable for any indirect,
            incidental or consequential damages arising from your use of this website or our
            services. Nothing in these terms excludes or limits liability that cannot be excluded
            under the Australian Consumer Law.
          </p>

          <h2>17. Privacy</h2>
          <p>
            Your use of this website is also governed by our{' '}
            <Link to="/privacy">Privacy Policy</Link>, which explains how we collect, use and
            protect your personal information.
          </p>

          <h2>18. Governing Law</h2>
          <p>
            These terms are governed by the laws of the State of Victoria, Australia. Any disputes
            will be subject to the exclusive jurisdiction of the courts of Victoria.
          </p>

          <h2>19. Changes to These Terms</h2>
          <p>
            We may update these terms from time to time. Changes take effect when posted on this
            page. Continued use of the website after changes constitutes acceptance of the
            updated terms.
          </p>

          <h2>20. Contact Us</h2>
          <p>For questions about these terms, contact us:</p>
          <ul>
            <li>Email: <a href="mailto:info@asgshading.com.au">info@asgshading.com.au</a></li>
            <li>Phone: <a href="tel:+61422439499">0422 439 499</a></li>
            <li>ABN: 54 658 871 369</li>
          </ul>

          <div className="legal-back">
            <Link to="/">← Back to Home</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default TermsPage;
